import { RouteComponentProps } from "react-router-dom";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Spinner from "react-bootstrap/Spinner";
import { Link } from "react-router-dom";
import { IonText, useIonToast } from "@ionic/react";
import { User, getAuth, createUserWithEmailAndPassword } from "firebase/auth";

import { deserialize } from "../../utils/serialize";
import Auth from "../../api/auth";

import styles from "./styles.module.css";
import SkeletonLoader from "../../components/SkeletonLoader";
import { useAppStore } from "../../store/app";
import Divider from "./Divider";
import GoogleAuthButton from "../../components/common/google-auth";
import { auth, signInWithToken } from "../../lib/firebase";
// import MicrosoftAuthButton from "../../components/common/microsoft-auth";
import { useAuth } from "../../providers/auth";

interface UserInviteForm {
  password: string;
  token: string;
  email: string;
}

const UserInviteRegister: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const [show, setShow] = useState(false);
  const [isError, setIsError] = useState(false);
  const [present] = useIonToast();
  const intl = useIntl();

  const user = useAppStore((state) => state.user);
  const { language } = user;

  const [formData, setFormData] = useState<UserInviteForm>({
    password: "",
    token: "",
    email: "",
  });

  const { refreshUser } = useAuth();

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);

  useEffect(() => {
    const { search } = props.location;
    const { token } = deserialize(search);

    if (token) {
      requestCodeInfo(token);
    }

    setFormData((prevFormData) => ({
      ...prevFormData,
      token,
    }));
  }, [props.location]);

  const register = async (
    auth: User,
    type?: "password" | "google" | "microsoft",
  ) => {
    //console.log("Registering user with type:", type);
    setLoading(true);
    const { data, err } = await Auth.registerUser({
      token: formData.token,
      language,
      auth: {
        provider: type || "password",
        uid: auth.uid,
      },
    });
    if (data) {
      // is a registration without auto-login
      refreshUser(); // refetch user after registration in DB succeded
    }

    if (err) {
      const { error } = err;
      if (error && error[language]) {
        setError(error[language]);
      } else {
        setError(err.message);
      }
      // Delete the user from firebase if somehting went wrong
      await auth?.delete();
    }
    setLoading(false);
  };

  const requestCodeInfo = async (token: string) => {
    setCodeLoading(true);
    const { data, err } = await Auth.getRegisterUserInfoToken(token);
    if (err || !data?.ok || !data?.email) {
      setCodeLoading(false);
      setIsError(true);
      return present({
        message:
          err?.error?.[language] ||
          intl.formatMessage({ id: "common.tryItLater" }),
        duration: 10000,
        color: "danger",
        position: "top",
        cssClass: "error-toast",
      });
    }

    setFormData((prevFormData) => ({
      ...prevFormData,
      email: data.email,
    }));
    setCodeLoading(false);
  };

  const handleForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setLoading(true);
    try {
      const { user } = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password,
      );
      if (user && user.uid) {
        await register(user, "password");
      }
    } catch (err: any) {
      setLoading(false);
      present({
        message:
          err?.message || intl.formatMessage({ id: "common.tryItLater" }),
        duration: 6000,
        color: "light",
        position: "top",
        cssClass: "error-toast",
      });
    }
  };

  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { value, id } = e.currentTarget;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [id]: value,
    }));
  };

  return isError ? null : (
    <div className={styles.authHolder}>
      <div className={styles.authPanel}>
        <h1>
          <FormattedMessage id="auth.registerUser.title" />
        </h1>
        <p className={styles.registerSubtitle}>
          <FormattedMessage id="auth.registerUser.subtitle" />
        </p>
        <div className="row">
          <div className="col-md-12">
            {codeLoading ? (
              <SkeletonLoader rows={2} />
            ) : (
              <>
                <div className={styles.inviteEmail}>{formData.email}</div>

                <p className={styles.registerOptionText}>
                  <FormattedMessage id="auth.registerUser.googleOption" />
                </p>
                <div className="flex flex-column gap-3">
                  <GoogleAuthButton
                    type="sign-up"
                    onSuccess={register}
                    email={formData.email}
                  />
                  {/*
                  <MicrosoftAuthButton
                    type="sign-up"
                    onSuccess={register}
                  />
                  */}
                </div>

                <Divider />

                <form className={styles.authForm} onSubmit={handleForm}>
                  <p
                    className={`${styles.registerOptionText} ${styles.passwordOptionText}`}
                  >
                    <FormattedMessage id="auth.registerUser.passwordOption" />
                  </p>
                  <div
                    className={`${styles.inputHolder} ${styles.passwordHolder}`}
                  >
                    <label>
                      <FormattedMessage id="auth.register.password" />
                    </label>
                    <input
                      placeholder="Password"
                      type={show ? "text" : "password"}
                      className="form-control"
                      id="password"
                      value={formData.password}
                      onChange={(e) => handleChange(e)}
                      required
                    />
                    <span
                      onClick={() => setShow(!show)}
                      className={styles.togglePassword}
                    ></span>
                  </div>

                  {error && (
                    <p className="mb-2 mt-2">
                      <IonText color="danger">{error}</IonText>
                    </p>
                  )}

                  <button
                    type="submit"
                    className="btn btn-visito-primary w-100 py-8 mt-4 mb-4 rounded-2"
                  >
                    {loading ? (
                      <Spinner animation="border" role="status" size="sm">
                        <span className="visually-hidden">
                          <FormattedMessage id="common.loading" />
                          ...
                        </span>
                      </Spinner>
                    ) : (
                      <FormattedMessage id="auth.register.register" />
                    )}
                  </button>
                  {/*

                  <div className="text-center mt-4">
                    <div className="color-step-500">
                      <FormattedMessage id="auth.register.loginLabel" />
                    </div>
                    <div>
                      <Link to={"/auth/login"}>
                        <FormattedMessage id="auth.register.login" />
                      </Link>
                    </div>
                  </div>
                  */}
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInviteRegister;
